import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserAddresses, getUserPaymentMethods, createOrder } from '../services/firestoreService';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 32px;
  margin-bottom: 30px;
  text-align: center;
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  margin-bottom: 30px;
  background-color: #ffffff;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
`;

/* Commented out to fix ESLint warning
const FormGroup = styled.div`
  margin-bottom: 20px;
`;
*/

/* Commented out to fix ESLint warning
const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;
*/

/* Commented out to fix ESLint error - may be needed in future
const Select = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  background-color: white;
`;
*/

const OrderSummary = styled.div`
  margin-top: 20px;
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemImage = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  margin-right: 15px;
`;

const ItemDetails = styled.div`
  flex-grow: 1;
`;

const ItemTitle = styled.h3`
  font-size: 16px;
  margin: 0 0 5px;
`;

const ItemVariant = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 5px;
`;

const ItemPrice = styled.p`
  font-weight: 500;
  margin: 0;
`;

const TotalSection = styled.div`
  margin-top: 20px;
  padding-top: 20px;
  border-top: 2px solid #eee;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 16px;
  
  &:last-child {
    font-size: 20px;
    font-weight: 700;
    margin-top: 10px;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  background-color: #000000;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  
  &:hover {
    background-color: #fff;
    color: #000000;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #d32f2f;
  margin: 10px 0;
`;

const AddressCard = styled.div`
  padding: 15px;
  border: 1px solid ${props => props.selected ? '#000' : '#ddd'};
  border-radius: 4px;
  margin-bottom: 15px;
  cursor: pointer;
  background-color: ${props => props.selected ? '#f9f9f9' : 'transparent'};
`;

const PaymentCard = styled.div`
  padding: 15px;
  border: 1px solid ${props => props.selected ? '#000' : '#ddd'};
  border-radius: 4px;
  margin-bottom: 15px;
  cursor: pointer;
  background-color: ${props => props.selected ? '#f9f9f9' : 'transparent'};
  display: flex;
  align-items: center;
`;

const CardIcon = styled.div`
  margin-right: 15px;
  font-size: 24px;
`;

const CardDetails = styled.div`
  flex-grow: 1;
`;

const Checkout = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  // Mock cart data - in a real app, this would come from your cart context/state
  // eslint-disable-next-line no-unused-vars
  const [cart, setCart] = useState({
    items: [
      {
        id: 'product123',
        variantId: 'variant456',
        title: 'Classic Black T-Shirt',
        size: 'L',
        color: 'Black',
        price: 39.99,
        quantity: 2,
        image: 'https://via.placeholder.com/80'
      },
      {
        id: 'product789',
        variantId: 'variant101',
        title: 'Slim Fit Jeans',
        size: '32',
        color: 'Blue',
        price: 49.99,
        quantity: 1,
        image: 'https://via.placeholder.com/80'
      }
    ]
  });
  
  const [addresses, setAddresses] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Calculate order totals
  const subtotal = cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const tax = subtotal * 0.08; // 8% tax example
  const shipping = subtotal > 100 ? 0 : 5.99; // Free shipping over $100
  const total = subtotal + tax + shipping;
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        try {
          // Fetch user addresses and payment methods
          const userAddresses = await getUserAddresses(currentUser.uid);
          const userPaymentMethods = await getUserPaymentMethods(currentUser.uid);
          
          setAddresses(userAddresses);
          setPaymentMethods(userPaymentMethods);
          
          // Set default selections if available
          const defaultAddress = userAddresses.find(addr => addr.default);
          const defaultPayment = userPaymentMethods.find(pm => pm.default);
          
          if (defaultAddress) setSelectedAddressId(defaultAddress.id);
          if (defaultPayment) setSelectedPaymentId(defaultPayment.id);
          
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError('Failed to load your saved information. Please try again.');
        }
      } else {
        // Redirect to login if not logged in
        navigate('/login', { state: { from: '/checkout' } });
      }
    };
    
    fetchUserData();
  }, [currentUser, navigate]);
  
  const handlePlaceOrder = async () => {
    if (!selectedAddressId || !selectedPaymentId) {
      setError('Please select a shipping address and payment method');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
      const selectedPayment = paymentMethods.find(pm => pm.id === selectedPaymentId);
      
      // Create order object
      const orderData = {
        userId: currentUser.uid,
        items: cart.items.map(item => ({
          productId: item.id,
          variantId: item.variantId,
          title: item.title,
          size: item.size,
          color: item.color,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        shippingAddress: {
          fullName: selectedAddress.fullName,
          line1: selectedAddress.line1,
          line2: selectedAddress.line2 || '',
          city: selectedAddress.city,
          state: selectedAddress.state,
          postalCode: selectedAddress.postalCode,
          country: selectedAddress.country,
          phone: selectedAddress.phone || ''
        },
        paymentMethod: {
          type: selectedPayment.type,
          last4: selectedPayment.last4,
          cardholderName: selectedPayment.cardholderName
        },
        subtotal,
        tax,
        shipping,
        total
      };
      
      // Create the order in Firestore
      const order = await createOrder(orderData);
      
      // Clear cart and redirect to order confirmation
      // clearCart(); // You would implement this in your cart context
      
      navigate(`/order-confirmation/${order.id}`);
      
    } catch (err) {
      console.error('Error placing order:', err);
      setError('Failed to place your order. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <Title>Checkout</Title>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <CheckoutGrid>
        <div>
          <Section>
            <SectionTitle>Shipping Address</SectionTitle>
            {addresses.length > 0 ? (
              addresses.map(address => (
                <AddressCard 
                  key={address.id}
                  selected={selectedAddressId === address.id}
                  onClick={() => setSelectedAddressId(address.id)}
                >
                  <p><strong>{address.fullName}</strong></p>
                  <p>{address.line1}</p>
                  {address.line2 && <p>{address.line2}</p>}
                  <p>{address.city}, {address.state} {address.postalCode}</p>
                  <p>{address.country}</p>
                </AddressCard>
              ))
            ) : (
              <p>No saved addresses. Please add an address in your account settings.</p>
            )}
          </Section>
          
          <Section>
            <SectionTitle>Payment Method</SectionTitle>
            {paymentMethods.length > 0 ? (
              paymentMethods.map(payment => (
                <PaymentCard
                  key={payment.id}
                  selected={selectedPaymentId === payment.id}
                  onClick={() => setSelectedPaymentId(payment.id)}
                >
                  <CardIcon>
                    {payment.type === 'visa' && '💳'}
                    {payment.type === 'mastercard' && '💳'}
                    {payment.type === 'amex' && '💳'}
                  </CardIcon>
                  <CardDetails>
                    <p><strong>{payment.type.toUpperCase()}</strong> ending in {payment.last4}</p>
                    <p>{payment.cardholderName}</p>
                    <p>Expires {payment.expMonth}/{payment.expYear}</p>
                  </CardDetails>
                </PaymentCard>
              ))
            ) : (
              <p>No saved payment methods. Please add a payment method in your account settings.</p>
            )}
          </Section>
        </div>
        
        <div>
          <Section>
            <SectionTitle>Order Summary</SectionTitle>
            <OrderSummary>
              {cart.items.map((item, index) => (
                <OrderItem key={index}>
                  <ItemImage src={item.image} alt={item.title} />
                  <ItemDetails>
                    <ItemTitle>{item.title}</ItemTitle>
                    <ItemVariant>Size: {item.size}, Color: {item.color}</ItemVariant>
                    <ItemVariant>Quantity: {item.quantity}</ItemVariant>
                  </ItemDetails>
                  <ItemPrice>${(item.price * item.quantity).toFixed(2)}</ItemPrice>
                </OrderItem>
              ))}
              
              <TotalSection>
                <TotalRow>
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </TotalRow>
                <TotalRow>
                  <span>Tax</span>
                  <span>${tax.toFixed(2)}</span>
                </TotalRow>
                <TotalRow>
                  <span>Shipping</span>
                  <span>${shipping.toFixed(2)}</span>
                </TotalRow>
                <TotalRow>
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </TotalRow>
              </TotalSection>
            </OrderSummary>
            
            <Button 
              onClick={handlePlaceOrder} 
              disabled={loading || !selectedAddressId || !selectedPaymentId}
            >
              {loading ? 'Processing...' : 'Place Order'}
            </Button>
          </Section>
        </div>
      </CheckoutGrid>
    </Container>
  );
};

export default Checkout;
